/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  CURRENT_SUB_PROFILE_ID_KEY,
  DEVICE_KEY,
  DUNNING_DIALOG_DISMISSED,
  PLAYER_SETTINGS_KEY,
  PRODUCTS_KEY,
  SESSION_KEY,
  SUBTITLE_SETTINGS,
  TV_TOKENS_KEY,
} from "@nf/constants";
import {
  ITokenModel,
  IUserDeviceModel,
  PlayerSettingsStorage,
  SubtitlesSettingsStorage,
} from "../models";
import { authStorage, StorageManager } from "../services";
import { CookieHelper } from "./CookieHelper";
import { BrowserHelper } from "./browserHelper";

// TODO Remove after 1 month of Bitmovin player release
const LEGACY_VOLUME_KEY = "playerVolume";
// TODO Remove after 1 month of Bitmovin player release
const LEGACY_REDBEE_PREFERENCES_KEY =
  "@redbeemedia/javascript-player:preferences";

interface IRedBeePlayerPreferences {
  audio?: { language?: string };
  subtitle?: { language?: string };
}

export class StorageHelper {
  static getSession(): ITokenModel | undefined {
    return authStorage.getSessionBehavior();
  }

  static setSession(session?: ITokenModel): void {
    if (!session) {
      return;
    }

    authStorage.setSessionBehavior(session);

    if (BrowserHelper.isTV()) {
      StorageManager.setValue(SESSION_KEY, session);
    }
  }

  static deleteSession(): Promise<void> {
    const deleteSessionPromise = authStorage.deleteSession();

    return BrowserHelper.isTV()
      ? StorageManager.deleteValue(SESSION_KEY)
      : deleteSessionPromise;
  }

  static getTvRefreshTokens(): Promise<string | undefined> {
    return StorageManager.getValue(TV_TOKENS_KEY);
  }
  static setTvRefreshTokens(tokens: string): Promise<void> {
    return StorageManager.setValue(TV_TOKENS_KEY, tokens);
  }

  static clearTvRefreshTokens(): Promise<void> {
    return StorageManager.deleteValue(TV_TOKENS_KEY);
  }

  static async clearUserData(): Promise<void[]> {
    return Promise.all([
      StorageManager.deleteValue(CURRENT_SUB_PROFILE_ID_KEY),
      StorageManager.deleteValue(TV_TOKENS_KEY),
      StorageManager.deleteValue(DUNNING_DIALOG_DISMISSED),
    ]);
  }

  static clearLocalStorage(): Promise<void[]> {
    authStorage.deleteSession();
    return Promise.all([
      StorageManager.deleteValue(CURRENT_SUB_PROFILE_ID_KEY),
      StorageManager.deleteValue(TV_TOKENS_KEY),
      StorageManager.deleteValue(DUNNING_DIALOG_DISMISSED),
      StorageHelper.deleteSession(),
    ]);
  }

  static async getDevice(): Promise<IUserDeviceModel> {
    return BrowserHelper.isTV()
      ? await StorageManager.getValue(DEVICE_KEY)
      : await CookieHelper.getDevice();
  }
  static setDevice(device: any): Promise<void> {
    return BrowserHelper.isTV()
      ? StorageManager.setValue(DEVICE_KEY, device)
      : CookieHelper.setDevice(device);
  }

  static getCurrentSubprofileId(): Promise<string | undefined> {
    return StorageManager.getValue(CURRENT_SUB_PROFILE_ID_KEY);
  }

  static setCurrentSubprofileId(subprofileId?: string): Promise<void> {
    return StorageManager.setValue(CURRENT_SUB_PROFILE_ID_KEY, subprofileId);
  }

  static deleteCurrentSubprofileId(): Promise<void> {
    return StorageManager.deleteValue(CURRENT_SUB_PROFILE_ID_KEY);
  }

  static async getPlayerSettings(): Promise<PlayerSettingsStorage | undefined> {
    const settings = await StorageManager.getValue<PlayerSettingsStorage>(
      PLAYER_SETTINGS_KEY
    );

    return settings ?? StorageHelper.migrateLegacyPlayerSettings();
  }

  // TODO Remove after 1 month of Bitmovin player release
  private static async migrateLegacyPlayerSettings(): Promise<
    PlayerSettingsStorage | undefined
  > {
    const [volume, redBeePreferences] = await Promise.all([
      StorageManager.getValue<number>(LEGACY_VOLUME_KEY),
      StorageManager.getValue<IRedBeePlayerPreferences>(
        LEGACY_REDBEE_PREFERENCES_KEY
      ),
    ]);

    const settings: PlayerSettingsStorage = {
      ...(typeof volume === "number" ? { volume } : {}),
      ...(redBeePreferences?.audio?.language
        ? { audioLanguage: redBeePreferences.audio.language }
        : {}),
      ...(redBeePreferences?.subtitle?.language
        ? { subtitleLanguage: redBeePreferences.subtitle.language }
        : {}),
    };

    if (!Object.keys(settings).length) {
      return undefined;
    }

    await StorageHelper.setPlayerSettings(settings);
    return settings;
  }

  static setPlayerSettings(settings: PlayerSettingsStorage): Promise<void> {
    return StorageManager.setValue(PLAYER_SETTINGS_KEY, settings);
  }

  static setProducts(value: string): Promise<void> {
    return StorageManager.setValue(PRODUCTS_KEY, value);
  }

  static getProducts(): Promise<string | undefined> {
    return StorageManager.getValue(PRODUCTS_KEY);
  }

  static deleteProducts(): Promise<void> {
    return StorageManager.deleteValue(PRODUCTS_KEY);
  }

  static getSubtitleSettings() {
    return StorageManager.getValue<SubtitlesSettingsStorage>(SUBTITLE_SETTINGS);
  }

  static setSubtitleSettings(settings: SubtitlesSettingsStorage) {
    return StorageManager.setValue(SUBTITLE_SETTINGS, settings);
  }
}
